import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { places, getPlace } from './places';
import MapCanvas from './MapCanvas';
import ExplorerSplash from './ExplorerSplash';
import { filterPlaces, visitorFilters, verification, routeTo } from './visitor-routing';
import './explorer.css';

const logo = 'https://BelvoirCastle.b-cdn.net/Logos/logo.webp';
const ADMIN_UNLOCK_KEY = 'belvoir-explore-admin';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'gardens', label: 'Gardens' },
  { id: 'retail', label: 'Retail Village' },
  { id: 'essentials', label: 'Facilities' },
];

export default function BelvoirExplorer({
  assetBase = '',
  initialPlace = 'castle',
  embedded = false,
}) {
  const start = getPlace(initialPlace) || places[0];
  const [selected, setSelected] = useState(start);
  const [category, setCategory] = useState('all');
  const [focusRequest, setFocusRequest] = useState(0);
  const [hunt, setHunt] = useState(false);
  const [found, setFound] = useState([]);
  const [status, setStatus] = useState('Opening the gardens');
  const [announcement, setAnnouncement] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [accessInfo, setAccessInfo] = useState(false);
  const [avoidSteps, setAvoidSteps] = useState(false);
  const [route, setRoute] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [loadKey, setLoadKey] = useState(0);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadPhase, setLoadPhase] = useState('loading');
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return window.sessionStorage.getItem(ADMIN_UNLOCK_KEY) === 'ok';
    } catch {
      return false;
    }
  });

  const results = filterPlaces(places, query, category, filter);
  const searchId = useId();
  const accessId = useId();
  const stepsId = useId();
  const huntId = useId();
  const note = verification(selected.id);

  const select = useCallback((id) => {
    const p = getPlace(id);
    if (!p) return;
    setRoute(null);
    setSelected(p);
    setCategory(p.category);
    setFocusRequest((n) => n + 1);
    setAnnouncement(`${p.name} selected.`);
  }, []);

  const find = useCallback((id) => {
    if (!getPlace(id)?.bee) return;
    setFound((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setAnnouncement(`You found the bee at ${getPlace(id).name}.`);
  }, []);

  const selectRef = useRef(select);
  selectRef.current = select;

  useEffect(() => {
    if (embedded) return undefined;
    const readHash = () => {
      const id = new URLSearchParams(window.location.hash.slice(1)).get('place');
      if (id && getPlace(id)) selectRef.current(id);
    };
    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, [embedded]);

  if (!unlocked) {
    return (
      <main className={`belvoir-explorer${embedded ? ' be-embedded' : ''}`} aria-label="Belvoir garden explorer">
        <ExplorerSplash
          gated
          onUnlock={() => {
            try {
              window.sessionStorage.setItem(ADMIN_UNLOCK_KEY, 'ok');
            } catch {
              /* preview still opens for this visit */
            }
            setUnlocked(true);
          }}
        />
      </main>
    );
  }

  return (
    <main className={`belvoir-explorer${embedded ? ' be-embedded' : ''}`} aria-label="Belvoir garden explorer">
      <ExplorerSplash
        key={loadKey}
        progress={loadProgress}
        phase={loadPhase}
        onRetry={() => {
          setLoadPhase('loading');
          setLoadProgress(0);
          setLoadKey((n) => n + 1);
        }}
      />
      <a className="be-skip" href="#belvoir-places">
        Skip to the place list
      </a>

      {!embedded && (
        <header className="be-header">
          <a className="be-brand" href="/">
            <img src={logo} alt="Belvoir Castle" />
          </a>
          <div className="be-header-centre">
            <span className="be-header-kicker">The garden explorer</span>
            <strong className="be-header-title">A little world to explore</strong>
          </div>
          <div className="be-header-links">
            <a href="/">Back to the website</a>
            <a className="be-header-cta" href="/your-visit/opening-times">
              Plan your visit
            </a>
          </div>
        </header>
      )}

      <div className="be-workspace">
        <aside className="be-sidebar" id="belvoir-places">
          <div className="be-sidebar-intro">
            <p className="be-eyebrow">Belvoir Castle &amp; Gardens</p>
            <h1>A little world to explore</h1>
            <p className="be-intro">
              Choose a place on the map, or from the list. Take a closer look at the castle, gardens, shops and hidden corners.
            </p>
          </div>

          <div className="be-search">
            <label htmlFor={searchId}>Find a place</label>
            <input
              id={searchId}
              type="search"
              placeholder="Search gardens, cafés, toilets…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setCategory('all');
              }}
            />
          </div>

          <div className="be-quick-filters" aria-label="Useful filters">
            {visitorFilters.map((f) => (
              <button
                key={f.id}
                type="button"
                aria-pressed={filter === f.id}
                onClick={() => {
                  setFilter(f.id);
                  setCategory('all');
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          <label className="be-access-toggle" htmlFor={accessId}>
            <input
              id={accessId}
              type="checkbox"
              checked={accessInfo}
              onChange={(e) => setAccessInfo(e.target.checked)}
            />
            Show accessibility information
          </label>

          <div className="be-tabs" role="tablist" aria-label="Types of places">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={category === tab.id}
                className={`be-tab${category === tab.id ? ' is-active' : ''}`}
                onClick={() => setCategory(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <p className="be-result-count" role="status">
            {results.length} {results.length === 1 ? 'place' : 'places'} found
          </p>

          <nav aria-label="Matching places" className="be-places">
            {results.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`be-place${selected.id === p.id ? ' is-active' : ''}`}
                aria-pressed={selected.id === p.id}
                onClick={() => select(p.id)}
              >
                <span>{p.number}</span>
                <span>{p.name}</span>
                <span>{found.includes(p.id) && hunt ? '✓' : '›'}</span>
              </button>
            ))}
          </nav>

          {results.length === 0 && (
            <div className="be-empty">
              <p>No matching places. Try a different name or filter.</p>
              <button
                type="button"
                className="be-outline"
                onClick={() => {
                  setQuery('');
                  setFilter('all');
                  setCategory('all');
                }}
              >
                Clear search & filters
              </button>
            </div>
          )}

          <section className="be-detail" aria-live="polite" aria-atomic="true">
            <p className="be-eyebrow">{selected.kind}</p>
            <h2>{selected.name}</h2>
            <p>{selected.description}</p>
            <p>{selected.detail}</p>
            <a className="be-source" href={selected.source} target="_blank" rel="noreferrer">
              About this place ↗
            </a>
            <div className="be-verification">
              <strong>{note.label}</strong>
              <p>{note.detail}</p>
            </div>

            <div className="be-route-panel">
              <h3>From the ticket entrance</h3>
              <label htmlFor={stepsId}>
                <input
                  id={stepsId}
                  type="checkbox"
                  checked={avoidSteps}
                  onChange={(e) => {
                    setAvoidSteps(e.target.checked);
                    if (route) setRoute(routeTo(selected, e.target.checked));
                  }}
                />
                Avoid mapped steps
              </label>
              <small>This does not confirm a step-free route.</small>
              <button
                type="button"
                className="be-primary"
                onClick={() => {
                  setRoute(routeTo(selected, avoidSteps));
                  setAnnouncement(`Route preview requested for ${selected.name}`);
                }}
              >
                Take me there
              </button>
              {route && (
                <div role="status">
                  {route.error ? (
                    <p>{route.error}</p>
                  ) : (
                    <>
                      <strong>
                        {route.metres < 10 ? 'You’re near the entrance' : `About ${route.minutes} min · ${route.metres} m`}
                      </strong>
                      <p>Route preview along mapped paths. Not yet approved by the estate.</p>
                      {route.gap > 4 && (
                        <p>
                          Stops on the nearest mapped path, about {route.gap} m from the place marker. The final approach is
                          unconfirmed.
                        </p>
                      )}
                      <small>
                        Walking time assumes 4 km/h and excludes stops, gradients and the final approach.
                      </small>
                      <p>
                        Mapped surfaces:{' '}
                        {route.surfaces.length ? route.surfaces.join(', ') : 'not recorded'}
                        {route.unknownSurface && route.surfaces.length ? ' · some sections unknown' : ''}.
                      </p>
                      {route.steps && <p>Includes mapped steps.</p>}
                      {route.slope && <p>A slope is marked; its steepness is unconfirmed.</p>}
                      <p>Check signs and current access with the visitor team.</p>
                    </>
                  )}
                  <button type="button" className="be-outline" onClick={() => setRoute(null)}>
                    Clear route
                  </button>
                </div>
              )}
            </div>

            {accessInfo && (
              <div className="be-access-panel">
                <h3>Accessibility at {selected.name}</h3>
                <dl>
                  <dt>Step-free access</dt>
                  <dd>Not confirmed</dd>
                  <dt>Steps & steep slopes</dt>
                  <dd>Estate assessment needed; missing map tags do not mean level ground.</dd>
                  <dt>Path surfaces</dt>
                  <dd>
                    {route && !route.error && route.surfaces.length
                      ? `Mapped nearby route: ${route.surfaces.join(', ')}. Condition unconfirmed.`
                      : 'Select “Take me there” for any mapped surface information.'}
                  </dd>
                  {selected.id === 'toilets' && (
                    <>
                      <dt>Accessible toilet provision</dt>
                      <dd>Not confirmed by the estate.</dd>
                    </>
                  )}
                </dl>
                <a href="/your-visit" target="_blank" rel="noreferrer">
                  Ask the visitor team about access ↗
                </a>
              </div>
            )}

            {hunt && selected.bee && (
              <div className="be-discovery">
                <div>
                  <strong>{found.includes(selected.id) ? 'A little discovery' : 'There’s a bee to find here'}</strong>
                  {found.includes(selected.id) ? (
                    <p>{selected.bee}</p>
                  ) : (
                    <button type="button" onClick={() => find(selected.id)}>
                      Find this garden bee
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>

          <div className="be-hunt">
            <label htmlFor={huntId}>
              <strong>Garden bee hunt</strong>
              <span>{hunt ? `${found.length} of 5 discovered` : 'A little adventure for curious minds'}</span>
            </label>
            <input
              id={huntId}
              type="checkbox"
              checked={hunt}
              onChange={(e) => {
                setHunt(e.target.checked);
                if (e.target.checked) setCategory('gardens');
              }}
            />
          </div>
          {hunt && (
            <div className="be-hunt-detail" aria-live="polite">
              {found.length === 5
                ? 'You found all five! A whole garden of discoveries.'
                : 'Find the golden bee markers on this digital map, or use the place list.'}
              {found.length > 0 && (
                <button
                  type="button"
                  className="be-link"
                  onClick={() => {
                    setFound([]);
                    setAnnouncement('Bee hunt restarted.');
                  }}
                >
                  Start again
                </button>
              )}
            </div>
          )}
        </aside>

        <section className="be-map" aria-label="Interactive 3D map">
          <MapCanvas
            key={loadKey}
            assetBase={assetBase.replace(/\/$/, '')}
            selected={selected.id}
            focusRequest={focusRequest}
            category={category}
            visibleIds={results.map((p) => p.id)}
            route={route}
            hunt={hunt}
            found={found}
            onSelect={select}
            onFind={find}
            onStatus={setStatus}
            onProgress={setLoadProgress}
            onLoadPhase={setLoadPhase}
          />
        </section>
      </div>

      <footer className="be-footer">
        <span className="be-status">
          <span aria-hidden="true" />
          Route previews · estate verification pending
        </span>
        <div className="be-footer-links">
          <button type="button" className="be-link" onClick={() => setDialog('how')}>
            How to explore
          </button>
          <button type="button" className="be-link" onClick={() => setDialog('info')}>
            Map information
          </button>
          <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
            © OpenStreetMap
          </a>
        </div>
      </footer>

      {dialog && (
        <div className="be-dialog-backdrop" role="presentation" onClick={() => setDialog(null)}>
          <div
            className="be-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="be-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            {dialog === 'how' ? (
              <>
                <h2 id="be-dialog-title">Make yourself at home</h2>
                <p>Explore with the map, or use the place list for the same stories and discoveries.</p>
                <dl>
                  <dt>Mouse or trackpad</dt>
                  <dd>Drag to rotate. Scroll to zoom. Right-drag to move across the map.</dd>
                  <dt>Touch</dt>
                  <dd>One finger rotates. Two fingers move and pinch to zoom.</dd>
                  <dt>Keyboard</dt>
                  <dd>
                    Focus the map, then use arrow keys to rotate and tilt, + and − to zoom, and Home to reset. All places
                    are also buttons in the list.
                  </dd>
                  <dt>Motion</dt>
                  <dd>Camera movement respects your device’s reduced-motion preference. Orbit is optional and can be paused.</dd>
                </dl>
              </>
            ) : (
              <>
                <h2 id="be-dialog-title">About this garden map</h2>
                <p>An illustrated explorer of selected places at Belvoir, based on the estate’s Blender model and OpenStreetMap geometry.</p>
                <p>
                  <strong>Estate verification: 0 of {places.length} places approved against an estate plan.</strong> Owner
                  identification and public mapping are recorded separately from estate approval.
                </p>
                <details>
                  <summary>Location review status</summary>
                  <ul>
                    {places.map((p) => (
                      <li key={p.id}>
                        {p.name}: {verification(p.id).label.toLowerCase()} · approval pending
                      </li>
                    ))}
                  </ul>
                </details>
                <p>
                  The terrain, architecture and planting are artistic interpretations. Not all gardens are included. Paths
                  shown do not establish public access or a recommended route.
                </p>
                <p>
                  For step-free access, facilities, closures and current opening times, check with the visitor team and
                  follow signs on site.
                </p>
                <a href="/your-visit/directions">Official visitor information ↗</a>
                <p className="be-map-date">
                  Path connections and available access tags checked 11 September 2026. Estate approval pending.
                </p>
                <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
                  © OpenStreetMap contributors · ODbL
                </a>
              </>
            )}
            <button type="button" className="be-primary" onClick={() => setDialog(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      <div className="sr-only" role="status" aria-live="polite">
        {status} {announcement}
      </div>
    </main>
  );
}
